import React from "react";
import { Modal, Alert, Spin, Tag, Input } from "antd";
import {
  AudioOutlined,
  LoadingOutlined,
  CloseOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  RiseOutlined,
  FallOutlined,
  DollarOutlined,
  CheckOutlined,
  RedoOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  UserOutlined,
  PhoneOutlined,
  SendOutlined,
} from "@ant-design/icons";
import { Button } from "antd";
import {
  useVoiceAssistant,
  VoiceAssistantState,
  AssistantResult,
  RevenueResult,
  ExpensesResult,
  ProfitResult,
  EmployeeRateResult,
  EmployeeSalaryResult,
  AnalysisResult,
  QueryMeta,
  PaymentBreakdown,
  ExpenseCategory,
  SalaryRates,
  LocationRates,
  ReservationPreview,
} from "./useVoiceAssistant";
import "./VoiceAssistantModal.scss";

interface Props {
  clientId: string;
  onClose: () => void;
}

const PAYMENT_ICONS: Record<string, string> = {
  cashAmount: "💵",
  card: "💳",
  delivery: "🛵",
  bankTransfer: "🏦",
};

function fmt(n: number) {
  return new Intl.NumberFormat("sr-RS", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}

export default function VoiceAssistantModal({ clientId, onClose }: Props) {
  const {
    state,
    lang,
    setLang,
    startListening,
    stopListening,
    sendQuery,
    confirmReservation,
    reset,
  } = useVoiceAssistant(clientId);
  const [guestPhone, setGuestPhone] = React.useState("");
  const [textInput, setTextInput] = React.useState("");

  const handleTextSubmit = () => {
    const q = textInput.trim();
    if (!q) return;
    setTextInput("");
    sendQuery(q, "text");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const renderContent = (s: VoiceAssistantState) => {
    switch (s.status) {
      case "idle":
        return (
          <div className="vam-idle">
            <p className="vam-hint">Šta možete pitati:</p>

            <div className="vam-capability-list">
              <div className="vam-capability-group">
                <div className="vam-cap-header vam-cap-revenue">
                  <RiseOutlined /> Prihodi
                </div>
                <div className="vam-cap-body">
                  <p className="vam-cap-desc">
                    Ukupna zarada, načini plaćanja i kategorije prihoda za
                    odabrani period.
                  </p>
                  <ul>
                    <li>"Prikaži zaradu za februar"</li>
                    <li>"Prihodi ovaj mesec lokacija Novi Sad"</li>
                    <li>"Zarada ove godine"</li>
                  </ul>
                </div>
              </div>

              <div className="vam-capability-group">
                <div className="vam-cap-header vam-cap-expenses">
                  <FallOutlined /> Rashodi
                </div>
                <div className="vam-cap-body">
                  <p className="vam-cap-desc">
                    Fiksni troškovi, dnevni rashodi i odbici iz prometa.
                  </p>
                  <ul>
                    <li>"Rashodi za mart"</li>
                    <li>"Troškovi prošli mesec lokacija Beograd"</li>
                    <li>"Koliko smo potrošili ove godine"</li>
                  </ul>
                </div>
              </div>

              <div className="vam-capability-group">
                <div className="vam-cap-header vam-cap-profit">
                  <DollarOutlined /> Profit
                </div>
                <div className="vam-cap-body">
                  <p className="vam-cap-desc">
                    Neto zarada, profitna marža i pregled prihoda vs. rashoda.
                  </p>
                  <ul>
                    <li>"Profit ovaj mesec"</li>
                    <li>"Prihod i rashod prošli mesec"</li>
                    <li>"Bilans za januar"</li>
                  </ul>
                </div>
              </div>

              <div className="vam-capability-group">
                <div className="vam-cap-header vam-cap-employee">
                  👤 Stope zaposlennog
                </div>
                <div className="vam-cap-body">
                  <p className="vam-cap-desc">
                    Satnica, dnevnica, procenat i fiksna plata — po lokacijama.
                  </p>
                  <ul>
                    <li>"Kolika je satnica Marka Jovića"</li>
                    <li>"Dnevnica Ane Petrović"</li>
                    <li>"Procenat zaposlenog Nikola"</li>
                  </ul>
                </div>
              </div>

              <div className="vam-capability-group">
                <div className="vam-cap-header vam-cap-salary">
                  💰 Plata zaposlennog
                </div>
                <div className="vam-cap-body">
                  <p className="vam-cap-desc">
                    Ukupna zarada za mesec, broj smjena i sati, pregled isplata.
                  </p>
                  <ul>
                    <li>"Kolika je plata Ane za januar"</li>
                    <li>"Koliko smjena je odradio Petar u martu"</li>
                    <li>"Zarada Nikole prošli mesec"</li>
                  </ul>
                </div>
              </div>

              <div className="vam-capability-group">
                <div className="vam-cap-header vam-cap-analysis">
                  🤖 AI Analiza
                </div>
                <div className="vam-cap-body">
                  <p className="vam-cap-desc">
                    AI komentar, poređenje perioda i konkretni prijedlozi za poboljšanje.
                  </p>
                  <ul>
                    <li>"Daj mi analizu posla za april"</li>
                    <li>"Uporedi rashode za februar, mart i april"</li>
                    <li>"Daj mi prijedlog šta da poboljšam da smanjim rashode"</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="vam-idle-note">
              Možete dodati <strong>lokaciju</strong> uz svaki upit o
              prihodima/rashodima.
              <br />
              Npr: <em>"… lokacija Novi Sad"</em>
            </div>

            <div className="vam-lang-toggle">
              <button
                className={`vam-lang-btn ${lang === "bcms" ? "vam-lang-active" : ""}`}
                onClick={() => setLang("bcms")}
              >
                SRB/BA/HR
              </button>
              <button
                className={`vam-lang-btn ${lang === "sl" ? "vam-lang-active" : ""}`}
                onClick={() => setLang("sl")}
              >
                SLO
              </button>
              <button
                className={`vam-lang-btn ${lang === "en" ? "vam-lang-active" : ""}`}
                onClick={() => setLang("en")}
              >
                EN
              </button>
            </div>

          </div>
        );

      case "listening":
        return (
          <div className="vam-listening">
            <div className="vam-lang-toggle">
              <button
                className={`vam-lang-btn ${lang === "bcms" ? "vam-lang-active" : ""}`}
                onClick={() => setLang("bcms")}
              >
                SRB/BA/HR
              </button>
              <button
                className={`vam-lang-btn ${lang === "sl" ? "vam-lang-active" : ""}`}
                onClick={() => setLang("sl")}
              >
                SLO
              </button>
              <button
                className={`vam-lang-btn ${lang === "en" ? "vam-lang-active" : ""}`}
                onClick={() => setLang("en")}
              >
                EN
              </button>
            </div>
            <div className="vam-pulse-ring">
              <div className="vam-pulse-dot">
                <AudioOutlined />
              </div>
            </div>
            <p className="vam-listening-label">
              Slušam… govorite pa pritisnite Gotovo
            </p>
            <div className="vam-live-transcript">
              {s.interimTranscript || "…"}
            </div>
            <Button
              type="primary"
              size="large"
              className="vam-done-btn"
              onClick={stopListening}
            >
              Gotovo
            </Button>
          </div>
        );

      case "parsing":
        return (
          <div className="vam-parsing">
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />}
            />
            <p>Analiziram: "{s.transcript}"</p>
          </div>
        );

      case "result":
        return <ResultView result={s.result} onAskAgain={startListening} />;

      case "reservation_preview":
        return (
          <ReservationPreviewView
            data={s.data}
            guestPhone={guestPhone || s.data.guestPhone || ""}
            onPhoneChange={setGuestPhone}
            onConfirm={async () => {
              const ok = await confirmReservation(
                s.data,
                guestPhone || s.data.guestPhone || "",
              );
              if (ok) setGuestPhone("");
            }}
            onRetry={() => {
              setGuestPhone("");
              startListening();
            }}
            onCancel={() => {
              setGuestPhone("");
              reset();
            }}
          />
        );

      case "saving_reservation":
        return (
          <div className="vam-parsing">
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />}
            />
            <p>Kreiram rezervaciju…</p>
          </div>
        );

      case "error":
        return (
          <div className="vam-error">
            <Alert type="error" showIcon message={s.message} />
            <div className="vam-actions" style={{ marginTop: 16 }}>
              <Button icon={<CloseOutlined />} onClick={reset}>
                Otkaži
              </Button>
              <Button
                type="primary"
                icon={<AudioOutlined />}
                onClick={startListening}
              >
                Pokušaj ponovo
              </Button>
            </div>
          </div>
        );
    }
  };

  const isListening = state.status === "listening";
  const isProcessing = state.status === "parsing" || state.status === "saving_reservation";

  return (
    <Modal
      open
      title={
        <span className="vam-title">
          <RiseOutlined style={{ marginRight: 8, color: "#10b981" }} />
          Business Assistant
        </span>
      }
      footer={null}
      onCancel={handleClose}
      width={520}
      className="voice-assistant-modal"
      wrapClassName="voice-assistant-modal-wrap"
      destroyOnClose
      style={{ maxWidth: "100vw" }}
    >
      <div className="vam-body">
        {renderContent(state)}
      </div>

      <div className="vam-footer">
        <div className="vam-text-input-row">
          <Input.Search
            className="vam-text-input"
            placeholder='Ukucajte pitanje, npr. "Analiza za april"'
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            onSearch={handleTextSubmit}
            onPressEnter={handleTextSubmit}
            enterButton={<SendOutlined />}
            size="large"
            allowClear
            disabled={isProcessing}
          />
        </div>
        <button
          className={`vam-mic-btn-footer ${isListening ? "vam-mic-btn-footer--active" : ""}`}
          onClick={isListening ? stopListening : startListening}
          disabled={isProcessing}
        >
          <AudioOutlined />
          {isListening ? "Zaustavi" : "Govori"}
        </button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Query context chips (shared)
// ---------------------------------------------------------------------------
function ContextChips({
  query,
  warnings,
}: {
  query: QueryMeta;
  warnings: string[];
}) {
  return (
    <>
      <div className="vam-result-context">
        <span className="vam-context-chip">
          <CalendarOutlined /> {query.period}
        </span>
        {query.locationName ? (
          <span className="vam-context-chip">
            <EnvironmentOutlined /> {query.locationName}
          </span>
        ) : (
          <span className="vam-context-chip vam-context-all">Sve lokacije</span>
        )}
      </div>
      {warnings.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message={warnings.join(" · ")}
          style={{ marginBottom: 12 }}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Breakdown list (shared)
// ---------------------------------------------------------------------------
function BreakdownList({
  title,
  items,
}: {
  title: string;
  items: React.ReactNode[];
}) {
  if (items.length === 0) return null;
  return (
    <div className="vam-breakdown">
      <div className="vam-breakdown-title">{title}</div>
      <div className="vam-breakdown-grid">{items}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Revenue result view
// ---------------------------------------------------------------------------
function RevenueResultView({
  result,
  onAskAgain,
}: {
  result: RevenueResult;
  onAskAgain: () => void;
}) {
  const { query, data, warnings } = result;
  return (
    <div className="vam-result">
      <ContextChips query={query} warnings={warnings} />

      <div className="vam-revenue-hero">
        <div className="vam-revenue-label">Ukupna zarada</div>
        <div className="vam-revenue-amount">
          {fmt(data.totalRevenue)} <span className="vam-currency">RSD</span>
        </div>
      </div>

      <BreakdownList
        title="Načini plaćanja"
        items={data.staticPayments.map((p: PaymentBreakdown) => (
          <div key={p.type} className="vam-breakdown-item">
            <span className="vam-breakdown-icon">
              {PAYMENT_ICONS[p.type] || "💰"}
            </span>
            <div className="vam-breakdown-info">
              <span className="vam-breakdown-name">{p.label}</span>
              <span className="vam-breakdown-val">{fmt(p.total)} RSD</span>
            </div>
            <Tag className="vam-pct-tag">{p.percentage}%</Tag>
          </div>
        ))}
      />

      <BreakdownList
        title="Kategorije prihoda"
        items={data.dynamicCategories.map((c) => (
          <div key={c.id} className="vam-breakdown-item">
            <span
              className="vam-breakdown-dot"
              style={{ background: c.color }}
            />
            <div className="vam-breakdown-info">
              <span className="vam-breakdown-name">{c.name}</span>
              <span className="vam-breakdown-val">{fmt(c.total)} RSD</span>
            </div>
            <Tag className="vam-pct-tag">{c.percentage}%</Tag>
          </div>
        ))}
      />

      <div className="vam-actions" style={{ marginTop: 20 }}>
        <Button icon={<AudioOutlined />} onClick={onAskAgain}>
          Pitaj ponovo
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Expenses result view
// ---------------------------------------------------------------------------
function ExpensesResultView({
  result,
  onAskAgain,
}: {
  result: ExpensesResult;
  onAskAgain: () => void;
}) {
  const { query, data, warnings } = result;
  return (
    <div className="vam-result">
      <ContextChips query={query} warnings={warnings} />

      <div className="vam-expenses-hero">
        <div className="vam-expenses-label">
          <FallOutlined style={{ marginRight: 6 }} />
          Ukupni rashodi
        </div>
        <div className="vam-expenses-amount">
          {fmt(data.totalExpenses)} <span className="vam-currency">RSD</span>
        </div>
        {data.customValuesEurTotal > 0 && (
          <div className="vam-expenses-eur">
            + {fmt(data.customValuesEurTotal)} EUR
          </div>
        )}
      </div>

      {(data.customValuesTotal > 0 ||
        data.dailyExpensesTotal > 0 ||
        data.deductionsTotal > 0) && (
        <div className="vam-breakdown">
          <div className="vam-breakdown-title">Pregled troškova</div>
          <div className="vam-breakdown-grid">
            {data.customValuesTotal > 0 && (
              <div className="vam-breakdown-item">
                <span className="vam-breakdown-icon">📋</span>
                <div className="vam-breakdown-info">
                  <span className="vam-breakdown-name">Fiksni troškovi</span>
                  <span className="vam-breakdown-val">
                    {fmt(data.customValuesTotal)} RSD
                  </span>
                </div>
              </div>
            )}
            {data.dailyExpensesTotal > 0 && (
              <div className="vam-breakdown-item">
                <span className="vam-breakdown-icon">🧾</span>
                <div className="vam-breakdown-info">
                  <span className="vam-breakdown-name">Dnevni rashodi</span>
                  <span className="vam-breakdown-val">
                    {fmt(data.dailyExpensesTotal)} RSD
                  </span>
                </div>
              </div>
            )}
            {data.deductionsTotal > 0 && (
              <div className="vam-breakdown-item">
                <span className="vam-breakdown-icon">➖</span>
                <div className="vam-breakdown-info">
                  <span className="vam-breakdown-name">
                    Odbici iz dnevnog prometa
                  </span>
                  <span className="vam-breakdown-val">
                    {fmt(data.deductionsTotal)} RSD
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <BreakdownList
        title="Top kategorije"
        items={data.categories.map((c: ExpenseCategory, i: number) => (
          <div key={i} className="vam-breakdown-item">
            <span className="vam-breakdown-icon">📌</span>
            <div className="vam-breakdown-info">
              <span className="vam-breakdown-name">{c.name}</span>
              <span className="vam-breakdown-val">{fmt(c.total)} RSD</span>
            </div>
          </div>
        ))}
      />

      <div className="vam-actions" style={{ marginTop: 20 }}>
        <Button icon={<AudioOutlined />} onClick={onAskAgain}>
          Pitaj ponovo
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Profit result view
// ---------------------------------------------------------------------------
function ProfitResultView({
  result,
  onAskAgain,
}: {
  result: ProfitResult;
  onAskAgain: () => void;
}) {
  const { query, data, warnings } = result;
  const isPositive = data.profit >= 0;

  return (
    <div className="vam-result">
      <ContextChips query={query} warnings={warnings} />

      {/* Profit hero */}
      <div
        className={`vam-profit-hero ${isPositive ? "vam-profit-positive" : "vam-profit-negative"}`}
      >
        <div className="vam-profit-label">
          {isPositive ? (
            <RiseOutlined style={{ marginRight: 6 }} />
          ) : (
            <FallOutlined style={{ marginRight: 6 }} />
          )}
          Profit
        </div>
        <div className="vam-profit-amount">
          {isPositive ? "+" : ""}
          {fmt(data.profit)} <span className="vam-currency">RSD</span>
        </div>
        <div className="vam-profit-margin">Marža: {data.profitMargin}%</div>
      </div>

      {/* Revenue vs Expenses comparison */}
      <div className="vam-breakdown">
        <div className="vam-breakdown-title">Pregled</div>
        <div className="vam-breakdown-grid">
          <div className="vam-breakdown-item vam-item-revenue">
            <span className="vam-breakdown-icon">
              <DollarOutlined />
            </span>
            <div className="vam-breakdown-info">
              <span className="vam-breakdown-name">Prihodi</span>
              <span className="vam-breakdown-val">
                {fmt(data.totalRevenue)} RSD
              </span>
            </div>
            <span className="vam-badge-green">↑</span>
          </div>
          <div className="vam-breakdown-item vam-item-expenses">
            <span className="vam-breakdown-icon">
              <FallOutlined />
            </span>
            <div className="vam-breakdown-info">
              <span className="vam-breakdown-name">Rashodi</span>
              <span className="vam-breakdown-val">
                {fmt(data.totalExpenses)} RSD
                {data.expenses.customValuesEurTotal > 0 && (
                  <span className="vam-eur-badge">
                    {" "}
                    + {fmt(data.expenses.customValuesEurTotal)} EUR
                  </span>
                )}
              </span>
            </div>
            <span className="vam-badge-red">↓</span>
          </div>
        </div>
      </div>

      {/* Payment methods */}
      <BreakdownList
        title="Načini plaćanja"
        items={data.revenue.staticPayments.map((p: PaymentBreakdown) => (
          <div key={p.type} className="vam-breakdown-item">
            <span className="vam-breakdown-icon">
              {PAYMENT_ICONS[p.type] || "💰"}
            </span>
            <div className="vam-breakdown-info">
              <span className="vam-breakdown-name">{p.label}</span>
              <span className="vam-breakdown-val">{fmt(p.total)} RSD</span>
            </div>
            <Tag className="vam-pct-tag">{p.percentage}%</Tag>
          </div>
        ))}
      />

      {/* Top expense categories */}
      <BreakdownList
        title="Top rashodi"
        items={data.expenses.categories.map((c: ExpenseCategory, i: number) => (
          <div key={i} className="vam-breakdown-item">
            <span className="vam-breakdown-icon">📌</span>
            <div className="vam-breakdown-info">
              <span className="vam-breakdown-name">{c.name}</span>
              <span className="vam-breakdown-val">{fmt(c.total)} RSD</span>
            </div>
          </div>
        ))}
      />

      <div className="vam-actions" style={{ marginTop: 20 }}>
        <Button icon={<AudioOutlined />} onClick={onAskAgain}>
          Pitaj ponovo
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Employee rate result view
// ---------------------------------------------------------------------------
function RatesList({ rates }: { rates: SalaryRates }) {
  const items = [
    { label: "Satnica", value: rates.hourlyRates, suffix: "RSD/h" },
    { label: "Procenat", value: rates.percentageRates, suffix: "%" },
    { label: "Dnevnica", value: rates.dailyWages, suffix: "RSD/dan" },
    { label: "Fiksna plata", value: rates.fixedSalaries, suffix: "RSD" },
    { label: "Ispomoc", value: rates.temporaryHelps, suffix: "RSD" },
    { label: "Putni troškovi", value: rates.travelExpenses, suffix: "RSD" },
  ].filter((r) => r.value != null && r.value > 0);

  if (items.length === 0)
    return (
      <p style={{ color: "#9ca3af", fontSize: 13, margin: "4px 0 8px" }}>
        Nema aktivnih stopa.
      </p>
    );

  return (
    <div className="vam-breakdown-grid">
      {items.map((r) => (
        <div key={r.label} className="vam-breakdown-item">
          <span className="vam-breakdown-icon">💼</span>
          <div className="vam-breakdown-info">
            <span className="vam-breakdown-name">{r.label}</span>
            <span className="vam-breakdown-val">
              {fmt(r.value!)} {r.suffix}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function EmployeeRateResultView({
  result,
  onAskAgain,
}: {
  result: EmployeeRateResult;
  onAskAgain: () => void;
}) {
  const { data, warnings } = result;
  const ratesByLocation: LocationRates[] = data.ratesByLocation ?? [];
  const multiLocation = ratesByLocation.length > 1;

  return (
    <div className="vam-result">
      {warnings.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message={warnings.join(" · ")}
          style={{ marginBottom: 12 }}
        />
      )}

      <div className="vam-revenue-hero">
        <div className="vam-revenue-label">👤 {data.employeeName}</div>
        <div
          className="vam-revenue-label"
          style={{ fontSize: 14, marginTop: 4, color: "#6b7280" }}
        >
          Trenutne stope zarade
        </div>
      </div>

      {ratesByLocation.length === 0 || !data.ratesByLocation ? (
        <p style={{ color: "#9ca3af", textAlign: "center", padding: "12px 0" }}>
          Nema unesenih stopa za ovog zaposlenog.
        </p>
      ) : multiLocation ? (
        ratesByLocation.map((loc: LocationRates, i: number) => (
          <div key={i} className="vam-breakdown">
            <div className="vam-breakdown-title">
              <EnvironmentOutlined style={{ marginRight: 6 }} />
              {loc.locationName || "Bez lokacije"}
            </div>
            <RatesList rates={loc.rates} />
          </div>
        ))
      ) : (
        <div className="vam-breakdown">
          <div className="vam-breakdown-title">Stope</div>
          <RatesList rates={ratesByLocation[0].rates} />
        </div>
      )}

      <div className="vam-actions" style={{ marginTop: 20 }}>
        <Button icon={<AudioOutlined />} onClick={onAskAgain}>
          Pitaj ponovo
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Employee salary result view
// ---------------------------------------------------------------------------
function EmployeeSalaryResultView({
  result,
  onAskAgain,
}: {
  result: EmployeeSalaryResult;
  onAskAgain: () => void;
}) {
  const { query, data, warnings } = result;

  const earningItems = [
    { label: "Fiksna plata", value: data.fixed, icon: "💼" },
    { label: "Dnevnice/Sati", value: data.daily, icon: "📅" },
    { label: "Procenat", value: data.percentage, icon: "%" },
    { label: "Bonus", value: data.bonus, icon: "⭐" },
    { label: "Prekovremeni", value: data.overtime, icon: "⏱️" },
    ...(data.loanDeduction > 0 ? [{ label: "Pozajmnice (odbitak)", value: -data.loanDeduction, icon: "🔻" }] : []),
  ].filter((i) => i.value !== 0);

  return (
    <div className="vam-result">
      <div className="vam-result-context">
        <span className="vam-context-chip">
          <CalendarOutlined /> {query.period}
        </span>
        <span className="vam-context-chip">👤 {data.employeeName}</span>
      </div>
      {warnings.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message={warnings.join(" · ")}
          style={{ marginBottom: 12 }}
        />
      )}

      <div className="vam-revenue-hero">
        {data.loanDeduction > 0 ? (
          <>
            <div className="vam-revenue-label">Bruto zarada</div>
            <div className="vam-revenue-amount">
              {fmt(data.grossSalary)} <span className="vam-currency">RSD</span>
            </div>
            <div style={{ fontSize: 13, color: "#ef4444", marginTop: 6 }}>
              Pozajmnice: -{fmt(data.loanDeduction)} RSD
            </div>
            <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4, borderTop: "1px solid #e5e7eb", paddingTop: 6 }}>
              Neto (sa odbitkom): <strong style={{ color: "#1f2937" }}>{fmt(data.totalForMonth)} RSD</strong>
            </div>
          </>
        ) : (
          <>
            <div className="vam-revenue-label">Ukupna zarada</div>
            <div className="vam-revenue-amount">
              {fmt(data.totalForMonth)} <span className="vam-currency">RSD</span>
            </div>
          </>
        )}
        {data.penalty > 0 && (
          <div style={{ fontSize: 13, color: "#ef4444", marginTop: 4 }}>
            Penali: -{fmt(data.penalty)} RSD
          </div>
        )}
      </div>

      <div className="vam-breakdown">
        <div className="vam-breakdown-title">Smjene</div>
        <div className="vam-breakdown-grid">
          <div className="vam-breakdown-item">
            <span className="vam-breakdown-icon">📋</span>
            <div className="vam-breakdown-info">
              <span className="vam-breakdown-name">Broj smjena</span>
              <span className="vam-breakdown-val">{data.shiftCount}</span>
            </div>
          </div>
          {data.totalHours > 0 && (
            <div className="vam-breakdown-item">
              <span className="vam-breakdown-icon">⏱️</span>
              <div className="vam-breakdown-info">
                <span className="vam-breakdown-name">Ukupno sati</span>
                <span className="vam-breakdown-val">{data.totalHours} h</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {earningItems.length > 0 && (
        <div className="vam-breakdown">
          <div className="vam-breakdown-title">Pregled zarade</div>
          <div className="vam-breakdown-grid">
            {earningItems.map((item) => (
              <div key={item.label} className="vam-breakdown-item">
                <span className="vam-breakdown-icon">{item.icon}</span>
                <div className="vam-breakdown-info">
                  <span className="vam-breakdown-name">{item.label}</span>
                  <span className="vam-breakdown-val" style={item.value < 0 ? { color: "#ef4444" } : undefined}>
                    {item.value < 0 ? `-${fmt(Math.abs(item.value))}` : fmt(item.value)} RSD
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {(data.payoutToBank > 0 || data.cashAmount > 0) && (
        <div className="vam-breakdown">
          <div className="vam-breakdown-title">Isplate</div>
          <div className="vam-breakdown-grid">
            {data.payoutToBank > 0 && (
              <div className="vam-breakdown-item">
                <span className="vam-breakdown-icon">🏦</span>
                <div className="vam-breakdown-info">
                  <span className="vam-breakdown-name">Na račun</span>
                  <span className="vam-breakdown-val">
                    {fmt(data.payoutToBank)} RSD
                  </span>
                </div>
              </div>
            )}
            {data.cashAmount > 0 && (
              <div className="vam-breakdown-item">
                <span className="vam-breakdown-icon">💵</span>
                <div className="vam-breakdown-info">
                  <span className="vam-breakdown-name">U gotovini</span>
                  <span className="vam-breakdown-val">
                    {fmt(data.cashAmount)} RSD
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="vam-actions" style={{ marginTop: 20 }}>
        <Button icon={<AudioOutlined />} onClick={onAskAgain}>
          Pitaj ponovo
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AI Analysis result view
// ---------------------------------------------------------------------------
function AnalysisResultView({
  result,
  onAskAgain,
}: {
  result: AnalysisResult;
  onAskAgain: () => void;
}) {
  const { analysisType, analysisText, query, data } = result;

  const typeLabel =
    analysisType === "comparison"
      ? "🔀 Poređenje perioda"
      : analysisType === "suggestions"
      ? "💡 Prijedlozi za poboljšanje"
      : "📊 Finansijska analiza";

  const headerColor =
    analysisType === "suggestions"
      ? "linear-gradient(135deg,#f59e0b 0%,#d97706 100%)"
      : analysisType === "comparison"
      ? "linear-gradient(135deg,#6366f1 0%,#4f46e5 100%)"
      : "linear-gradient(135deg,#10b981 0%,#059669 100%)";

  // For comparison: show a mini table of periods
  const periodsTable =
    analysisType === "comparison" && data.periods && data.periods.length > 0 ? (
      <div className="vam-breakdown">
        <div className="vam-breakdown-title">Pregled perioda</div>
        <div className="vam-breakdown-grid">
          {data.periods.map((p, i) => {
            const profit = p.revenue.totalRevenue - p.expenses.totalExpenses;
            return (
              <div key={i} className="vam-breakdown-item">
                <span className="vam-breakdown-icon">📅</span>
                <div className="vam-breakdown-info">
                  <span className="vam-breakdown-name">{p.label}</span>
                  <span className="vam-breakdown-val">
                    {fmt(p.revenue.totalRevenue)} RSD prihoda
                  </span>
                  <span className="vam-breakdown-val" style={{ fontSize: 11, color: profit >= 0 ? "#10b981" : "#ef4444" }}>
                    Profit: {profit >= 0 ? "+" : ""}{fmt(profit)} RSD
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    ) : null;

  // For overview/suggestions: show revenue+expenses summary
  const summaryRow =
    analysisType !== "comparison" && data.revenue && data.expenses ? (
      <div className="vam-breakdown">
        <div className="vam-breakdown-grid">
          <div className="vam-breakdown-item vam-item-revenue">
            <span className="vam-breakdown-icon"><DollarOutlined /></span>
            <div className="vam-breakdown-info">
              <span className="vam-breakdown-name">Prihodi</span>
              <span className="vam-breakdown-val">{fmt(data.revenue.totalRevenue)} RSD</span>
            </div>
          </div>
          <div className="vam-breakdown-item vam-item-expenses">
            <span className="vam-breakdown-icon"><FallOutlined /></span>
            <div className="vam-breakdown-info">
              <span className="vam-breakdown-name">Rashodi</span>
              <span className="vam-breakdown-val">{fmt(data.expenses.totalExpenses)} RSD</span>
            </div>
          </div>
        </div>
      </div>
    ) : null;

  return (
    <div className="vam-result">
      {query && (
        <div className="vam-result-context">
          <span className="vam-context-chip"><CalendarOutlined /> {query.period}</span>
          {query.locationName && (
            <span className="vam-context-chip"><EnvironmentOutlined /> {query.locationName}</span>
          )}
        </div>
      )}

      <div className="vam-revenue-hero" style={{ background: headerColor }}>
        <div className="vam-revenue-label" style={{ color: "rgba(255,255,255,0.8)" }}>{typeLabel}</div>
        <div className="vam-revenue-amount" style={{ fontSize: 16, lineHeight: 1.4, fontWeight: 500 }}>
          🤖 AI komentar
        </div>
      </div>

      {periodsTable}
      {summaryRow}

      <div className="vam-analysis-text">
        {analysisText.split("\n").map((line, i) => (
          <p key={i} className={line.match(/^\d+\./) ? "vam-analysis-point" : "vam-analysis-para"}>
            {line}
          </p>
        ))}
      </div>

      <div className="vam-actions" style={{ marginTop: 20 }}>
        <Button icon={<AudioOutlined />} onClick={onAskAgain}>
          Pitaj ponovo
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Result dispatcher
// ---------------------------------------------------------------------------
function ResultView({
  result,
  onAskAgain,
}: {
  result: AssistantResult;
  onAskAgain: () => void;
}) {
  if (result.intent === "revenue")
    return <RevenueResultView result={result} onAskAgain={onAskAgain} />;
  if (result.intent === "expenses")
    return <ExpensesResultView result={result} onAskAgain={onAskAgain} />;
  if (result.intent === "employee_rate")
    return <EmployeeRateResultView result={result} onAskAgain={onAskAgain} />;
  if (result.intent === "employee_salary")
    return <EmployeeSalaryResultView result={result} onAskAgain={onAskAgain} />;
  if (result.intent === "analysis")
    return <AnalysisResultView result={result} onAskAgain={onAskAgain} />;
  return (
    <ProfitResultView result={result as ProfitResult} onAskAgain={onAskAgain} />
  );
}

// ---------------------------------------------------------------------------
// Reservation preview
// ---------------------------------------------------------------------------
function ReservationPreviewView({
  data,
  guestPhone,
  onPhoneChange,
  onConfirm,
  onRetry,
  onCancel,
}: {
  data: ReservationPreview;
  guestPhone: string;
  onPhoneChange: (v: string) => void;
  onConfirm: () => void;
  onRetry: () => void;
  onCancel: () => void;
}) {
  const dateLabel = (() => {
    try {
      return new Date(data.date + "T12:00:00").toLocaleDateString(
        "sr-Latn-RS",
        {
          weekday: "long",
          day: "numeric",
          month: "long",
          year: "numeric",
        },
      );
    } catch {
      return data.date;
    }
  })();

  return (
    <div className="vam-result">
      <div
        className="vam-revenue-hero"
        style={{
          background: "linear-gradient(135deg,#6366f1 0%,#4f46e5 100%)",
        }}
      >
        <div className="vam-revenue-label" style={{ color: "#c7d2fe" }}>
          Nova rezervacija
        </div>
        <div className="vam-revenue-amount" style={{ fontSize: 20 }}>
          {data.guestName}
        </div>
      </div>

      <div className="vam-breakdown">
        <div className="vam-breakdown-grid">
          <div className="vam-breakdown-item">
            <span className="vam-breakdown-icon">
              <CalendarOutlined />
            </span>
            <div className="vam-breakdown-info">
              <span className="vam-breakdown-name">Datum</span>
              <span className="vam-breakdown-val">{dateLabel}</span>
            </div>
          </div>
          <div className="vam-breakdown-item">
            <span className="vam-breakdown-icon">
              <ClockCircleOutlined />
            </span>
            <div className="vam-breakdown-info">
              <span className="vam-breakdown-name">Vrijeme</span>
              <span className="vam-breakdown-val">{data.time}</span>
            </div>
          </div>
          <div className="vam-breakdown-item">
            <span className="vam-breakdown-icon">
              <TeamOutlined />
            </span>
            <div className="vam-breakdown-info">
              <span className="vam-breakdown-name">Broj osoba</span>
              <span className="vam-breakdown-val">{data.numberOfGuests}</span>
            </div>
          </div>
          {data.note && (
            <div className="vam-breakdown-item">
              <span className="vam-breakdown-icon">📝</span>
              <div className="vam-breakdown-info">
                <span className="vam-breakdown-name">Napomena</span>
                <span className="vam-breakdown-val">{data.note}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label
          style={{
            display: "block",
            fontSize: 13,
            color: "#6b7280",
            marginBottom: 6,
          }}
        >
          <PhoneOutlined style={{ marginRight: 6 }} />
          Telefon gosta
        </label>
        <Input
          placeholder="+381 6x xxx xxxx"
          value={guestPhone}
          onChange={(e) => onPhoneChange(e.target.value)}
          size="large"
          style={{ borderRadius: 10 }}
        />
      </div>

      <div className="vam-actions">
        <Button icon={<CloseOutlined />} onClick={onCancel}>
          Otkaži
        </Button>
        <Button icon={<RedoOutlined />} onClick={onRetry}>
          Snimi ponovo
        </Button>
        <Button
          type="primary"
          icon={<CheckOutlined />}
          onClick={onConfirm}
          disabled={!guestPhone.trim()}
          style={{ background: "#6366f1", borderColor: "#6366f1" }}
        >
          Sačuvaj
        </Button>
      </div>
    </div>
  );
}
